/**
 * Field (employee mobile) capability catalog.
 *
 * SoT: docs/Tai-lieu-trien-khai/FE_Field_Employee_App_Architecture.md
 *
 * - UI only surfaces `status: "live"`.
 * - `planned` entries document expansion slots — do not render fake screens.
 * - Domain modules own pages; this file only wires chrome / hubs.
 */

import { hasAnyPermission, PermissionCodes } from "@/shared/auth/permissions";

export type FieldDomain =
  | "mes"
  | "qms"
  | "docs"
  | "wms"
  | "sys"
  | "hr"
  | "work"
  | "pur";

export type FieldCapabilityStatus = "live" | "planned";

export type FieldPlacement = "bottom-nav" | "account-hub" | "home-widget";

export type FieldAccountGroup = "work" | "account" | "ops" | "hr";

export type FieldCapabilityIcon =
  | "home"
  | "scan-line"
  | "clipboard-list"
  | "user"
  | "history"
  | "bell"
  | "settings"
  | "user-round"
  | "warehouse"
  | "calendar-clock"
  | "palm"
  | "layers"
  | "list-todo";

export type FieldCapability = {
  id: string;
  domain: FieldDomain;
  label: string;
  /** Stable product path when live (or target path when planned). */
  path: string;
  screenId?: string;
  /** BE dot-form; empty = authenticated. */
  permissions: string[];
  status: FieldCapabilityStatus;
  placement: FieldPlacement[];
  /** Group on `/field/me` when placement includes account-hub. */
  accountGroup?: FieldAccountGroup;
  detail?: string;
  icon: FieldCapabilityIcon;
  /** Offline queue candidate (FE-12) — informational for now. */
  offlineEligible?: boolean;
};

export const FIELD_ACCOUNT_GROUP_LABEL: Record<FieldAccountGroup, string> = {
  work: "Công việc",
  account: "Tài khoản",
  ops: "Vận hành",
  hr: "Nhân sự",
};

/**
 * Ordered catalog. Bottom-nav order = order of live items with placement bottom-nav.
 */
export const fieldCapabilityRegistry: FieldCapability[] = [
  // —— Bottom nav (Gói 1) ——
  {
    id: "field.home",
    domain: "sys",
    label: "Trang chủ",
    path: "/field/home",
    screenId: "SCR-FIELD-HOME",
    permissions: [],
    status: "live",
    placement: ["bottom-nav", "home-widget"],
    icon: "home",
  },
  {
    id: "field.batches",
    domain: "mes",
    label: "Mẻ SX",
    path: "/field/batches",
    screenId: "SCR-FIELD-BATCHES",
    permissions: [PermissionCodes.MesBatchRead],
    status: "live",
    placement: ["bottom-nav", "account-hub"],
    accountGroup: "work",
    detail: "Mẻ sản xuất & công đoạn",
    icon: "layers",
  },
  {
    id: "field.scan",
    domain: "mes",
    label: "Quét",
    path: "/field/scan",
    screenId: "SCR-MES-SCAN",
    permissions: [PermissionCodes.MesBatchRead],
    status: "live",
    placement: ["bottom-nav"],
    icon: "scan-line",
  },
  {
    id: "field.pur.requisitions",
    domain: "pur",
    label: "Đề nghị mua",
    path: "/field/pur/requisitions",
    screenId: "SCR-FIELD-PUR-PR",
    permissions: [PermissionCodes.PurchaseRequisitionList, PermissionCodes.PurchaseRequest],
    status: "live",
    placement: ["bottom-nav", "account-hub", "home-widget"],
    accountGroup: "work",
    detail: "Tạo & theo dõi đề nghị mua vật tư, NVL",
    icon: "clipboard-list",
  },
  {
    id: "field.cip",
    domain: "mes",
    label: "Vệ sinh chuyền",
    path: "/field/cip",
    screenId: "SCR-MES-CIP",
    permissions: [PermissionCodes.MesBatchRead, PermissionCodes.MesOperationExecute],
    status: "planned",
    placement: [],
    icon: "clipboard-list",
  },
  {
    id: "field.account",
    domain: "sys",
    label: "Tài khoản",
    path: "/field/me",
    screenId: "SCR-SYS-ACCOUNT",
    permissions: [],
    status: "live",
    placement: ["bottom-nav"],
    icon: "user",
  },

  // —— Account hub (live) ——
  {
    id: "field.mes.today",
    domain: "mes",
    label: "Hàng đợi hôm nay",
    path: "/field/today",
    screenId: "SCR-MES-TODAY",
    permissions: [PermissionCodes.MesBatchRead],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Công đoạn ca này",
    icon: "list-todo",
  },
  {
    id: "field.mes.activity",
    domain: "mes",
    label: "Việc tôi đã làm",
    path: "/field/activity",
    screenId: "SCR-MES-MY-ACTIVITY",
    permissions: [PermissionCodes.MesBatchRead],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Start / Complete của tôi",
    icon: "history",
  },
  {
    id: "field.mes.handover",
    domain: "mes",
    label: "Bàn giao",
    path: "/field/handover",
    screenId: "SCR-MES-HANDOVER",
    permissions: [PermissionCodes.MesBatchRead, PermissionCodes.MesOperationExecute],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Lot SFG giữa công đoạn",
    icon: "clipboard-list",
  },
  {
    id: "field.mes.rework",
    domain: "mes",
    label: "Gia công lại",
    path: "/field/rework",
    screenId: "SCR-MES-REWORK",
    permissions: [PermissionCodes.MesBatchRead, PermissionCodes.MesOperationExecute],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Lệnh rework · cây lô",
    icon: "history",
  },
  {
    id: "field.mes.usage",
    domain: "mes",
    label: "Hao hụt",
    path: "/field/usage",
    screenId: "SCR-MES-USAGE",
    permissions: [PermissionCodes.MesBatchRead],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Sổ hao hụt chế biến",
    icon: "list-todo",
  },
  {
    id: "field.qms.inbox",
    domain: "qms",
    label: "Chất lượng",
    path: "/field/qms",
    screenId: "SCR-FIELD-QMS",
    permissions: [PermissionCodes.QmsIqcList, PermissionCodes.QmsFqcList],
    status: "live",
    placement: ["account-hub", "home-widget"],
    accountGroup: "work",
    detail: "Kiểm tra chất lượng đầu vào (IQC) & đầu ra (FQC)",
    icon: "clipboard-list",
  },
  {
    id: "field.sys.profile",
    domain: "sys",
    label: "Hồ sơ",
    path: "/field/profile",
    screenId: "SCR-SYS-PROFILE",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    detail: "Họ tên · plant · vai trò · quyền",
    icon: "user-round",
  },
  {
    id: "field.sys.notify",
    domain: "sys",
    label: "Thông báo",
    path: "/field/notifications",
    screenId: "SCR-SYS-NOTIFY",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    detail: "Hộp thư trong app",
    icon: "bell",
  },
  {
    id: "field.sys.settings",
    domain: "sys",
    label: "Cài đặt ứng dụng",
    path: "/field/settings",
    screenId: "SCR-SYS-SETTINGS",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    detail: "Cỡ chữ, thông báo và đồng bộ",
    icon: "settings",
  },
  {
    id: "field.sys.sync",
    domain: "sys",
    label: "Đồng bộ",
    path: "/field/sync",
    screenId: "SCR-SYS-SYNC",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    detail: "Mạng · hàng đợi offline (stub)",
    icon: "settings",
  },

  // —— Planned expansion (not rendered) ——
  {
    id: "field.wms.home",
    domain: "wms",
    label: "Kho hiện trường",
    path: "/field/wms",
    screenId: "SCR-WMS-FIELD",
    permissions: [PermissionCodes.WmsStockRead],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Move · Count online",
    icon: "warehouse",
    offlineEligible: true,
  },

  {
    id: "field.hr.attendance",
    domain: "hr",
    label: "Chấm công",
    path: "/field/hr/attendance",
    permissions: ["hr.attendance.manage"],
    status: "planned",
    placement: ["account-hub", "home-widget"],
    accountGroup: "hr",
    detail: "Check-in / check-out",
    icon: "calendar-clock",
  },
  {
    id: "field.hr.leave",
    domain: "hr",
    label: "Xin phép",
    path: "/field/hr/leave",
    permissions: ["hr.leave.request"],
    status: "planned",
    placement: ["account-hub", "home-widget"],
    accountGroup: "hr",
    detail: "Yêu cầu nghỉ · trạng thái duyệt",
    icon: "palm",
  },
  {
    id: "field.work.tasks",
    domain: "work",
    label: "Việc được giao",
    path: "/field/work/tasks",
    permissions: ["work.task.read"],
    status: "planned",
    placement: ["account-hub", "home-widget"],
    accountGroup: "work",
    detail: "Task cá nhân / tổ",
    icon: "list-todo",
  },
];

export function liveFieldCapabilities(
  placement?: FieldPlacement,
): FieldCapability[] {
  return fieldCapabilityRegistry.filter(
    (c) =>
      c.status === "live" &&
      (placement == null || c.placement.includes(placement)),
  );
}

export function plannedFieldCapabilities(): FieldCapability[] {
  return fieldCapabilityRegistry.filter((c) => c.status === "planned");
}

export type FieldBottomNavItem = {
  id: string;
  label: string;
  route: string;
  icon: FieldCapabilityIcon;
};

/** Bottom nav items in registry order (live + bottom-nav + permission filter). */
export function fieldBottomNavItems(
  permissions: readonly string[] = [],
): FieldBottomNavItem[] {
  return liveFieldCapabilities("bottom-nav")
    .filter(
      (c) =>
        c.permissions.length === 0 ||
        hasAnyPermission(permissions, c.permissions),
    )
    .map((c) => ({
      id: c.id.replace(/^field\./, "").replace(/\./g, "-"),
      label: c.label,
      route: c.path,
      icon: c.icon,
    }));
}

export function accountHubGroups(
  permissions: string[],
  hasPermission: (codes: string[], required: string[]) => boolean,
): { group: FieldAccountGroup; label: string; items: FieldCapability[] }[] {
  const live = liveFieldCapabilities("account-hub").filter((c) => {
    if (c.permissions.length === 0) return true;
    return hasPermission(permissions, c.permissions);
  });

  const order: FieldAccountGroup[] = ["work", "ops", "hr", "account"];
  return order
    .map((group) => ({
      group,
      label: FIELD_ACCOUNT_GROUP_LABEL[group],
      items: live.filter((c) => c.accountGroup === group),
    }))
    .filter((g) => g.items.length > 0);
}
